// =============================================================================
// hooks/useKpi.js
//
// Hooks que abstraem o Redux dos componentes.
// O filtrado dos incidentes e CLIENT-SIDE (responde em tempo real).
// O backend devolve uma vez por refresh; depois tudo se filtra no browser.
// =============================================================================
import { useSelector, useDispatch } from 'react-redux';
import { useCallback, useMemo } from 'react';
import {
	fetchIncidents, fetchIncidentHistory, selectIncident, changeFilter, resetFilters,
} from '../actions/incidentActions';
import {
	fetchRules, saveRule, deleteRule, selectRule, resetRuleForm,
	validateFormula, resetValidation, fetchEngineLogs,
} from '../actions/kpiRuleActions';
import { fetchLookups } from '../actions/lookupActions';
import { fetchSites } from '../actions/siteActions';

// ============================================================================
// useLookups — dropdowns vindos da BD (vendors, techs, kpis, severities, statuses)
// ============================================================================
export function useLookups() {
	const dispatch = useDispatch();

	const lookups = useSelector(function (s) { return s.lookups; });

	const load = useCallback(function () {
		return dispatch(fetchLookups());
	}, [dispatch]);

	// Mapas auxiliares (acesso O(1) por valor)
	const severityMap = useMemo(function () {
		const map = {};
		(lookups.severities || []).forEach(function (s) { map[s.value] = s; });
		return map;
	}, [lookups.severities]);

	const statusMap = useMemo(function () {
		const map = {};
		(lookups.statuses || []).forEach(function (s) { map[s.value] = s; });
		return map;
	}, [lookups.statuses]);

	return {
		loading:     lookups.loading,
		loaded:      lookups.loaded,
		error:       lookups.error,
		vendors:     lookups.vendors,
		techs:       lookups.techs,
		kpis:        lookups.kpis,
		severities:  lookups.severities,
		statuses:    lookups.statuses,
		severityMap: severityMap,
		statusMap:   statusMap,
		load:        load,
	};
}

// ============================================================================
// useIncidents
// ============================================================================
export function useIncidents() {
	const dispatch = useDispatch();

	const items          = useSelector(function (s) { return s.incidents.items; });
	const loading        = useSelector(function (s) { return s.incidents.loading; });
	const error          = useSelector(function (s) { return s.incidents.error; });
	const selected       = useSelector(function (s) { return s.incidents.selected; });
	const history        = useSelector(function (s) { return s.incidents.history; });
	const historyLoading = useSelector(function (s) { return s.incidents.historyLoading; });
	const filters        = useSelector(function (s) { return s.incidents.filters; });

	// --- Actions ---------------------------------------------------------
	const load = useCallback(function () {
		// Devolve todos os incidentes sem filtros do backend. Filtragem fica client-side.
		return dispatch(fetchIncidents());
	}, [dispatch]);

	const select = useCallback(function (inc) {
		dispatch(selectIncident(inc));
		if (inc && inc.incidentId) {
			dispatch(fetchIncidentHistory(inc.incidentId));
		}
	}, [dispatch]);

	const updateFilters = useCallback(function (f) {
		dispatch(changeFilter(f));
	}, [dispatch]);

	const clearFilters = useCallback(function () {
		dispatch(resetFilters());
	}, [dispatch]);

	// --- Filtragem CLIENT-SIDE -------------------------------------------
	const filteredItems = useMemo(function () {
		if (!items || !items.length) return [];

		return items.filter(function (i) {
			// Status
			if (filters.status && filters.status !== 'all' && i.status !== filters.status) return false;

			// Tech
			if (filters.tech && filters.tech !== 'all' && i.tech !== filters.tech) return false;

			// Severity
			if (filters.severity != null && filters.severity !== 'all' && i.severity !== Number(filters.severity)) return false;

			// Vendor
			if (filters.vendor && filters.vendor !== 'all' && i.vendor !== filters.vendor) return false;

			// KPI (por kpiName)
			if (filters.kpiName && filters.kpiName !== 'all' && i.kpiName !== filters.kpiName) return false;

			// Site ID (substring match)
			if (filters.siteId && filters.siteId.length > 0) {
				const q = filters.siteId.toLowerCase();
				if (!i.siteId || i.siteId.toLowerCase().indexOf(q) === -1) return false;
			}

			// Cell ID search (substring)
			if (filters.cellId && filters.cellId.length > 0) {
				const q = filters.cellId.toLowerCase();
				if (!i.cellId || i.cellId.toLowerCase().indexOf(q) === -1) return false;
			}

			// Duration bucket
			if (filters.durationBucket && filters.durationBucket !== 'all') {
				const d = i.durationDays || 0;
				if (filters.durationBucket === 'lt1'  && !(d < 1))         return false;
				if (filters.durationBucket === '1to3' && !(d >= 1 && d <= 3)) return false;
				if (filters.durationBucket === '4to7' && !(d >= 4 && d <= 7)) return false;
				if (filters.durationBucket === 'gt7'  && !(d > 7))         return false;
			}

			return true;
		});
	}, [items, filters]);

	// --- Stats globais (sobre TODOS os items, antes dos filtros) ---------
	const totalStats = useMemo(function () {
		const total  = items.length;
		const open   = items.filter(function (i) { return i.status === 'Open'; }).length;
		const closed = items.filter(function (i) { return i.status === 'Closed'; }).length;
		const critical = items.filter(function (i) { return i.severity === 1 && i.status === 'Open'; }).length;

		// Avg duration of currently open incidents
		const openItems = items.filter(function (i) { return i.status === 'Open'; });
		const avgDuration = openItems.length === 0
			? 0
			: openItems.reduce(function (acc, i) { return acc + (i.durationDays || 0); }, 0) / openItems.length;

		return { total: total, open: open, closed: closed, critical: critical, avgDuration: avgDuration };
	}, [items]);

	// --- Stats sobre items FILTRADOS (para mostrar contagens parciais) ---
	const filteredStats = useMemo(function () {
		const total  = filteredItems.length;
		const open   = filteredItems.filter(function (i) { return i.status === 'Open'; }).length;
		const closed = filteredItems.filter(function (i) { return i.status === 'Closed'; }).length;
		return { total: total, open: open, closed: closed };
	}, [filteredItems]);

	// --- Distribuicao por KPI (com tech) ---------------------------------
	const kpiDistribution = useMemo(function () {
		// Conta incidentes abertos por (tech + kpiName) sobre items FILTRADOS
		// (excluindo o filtro de kpiName, para que o user veja todos os KPIs disponiveis)
		const itemsForDist = items.filter(function (i) {
			// Aplica todos os filtros EXCETO o de kpiName
			if (filters.status && filters.status !== 'all' && i.status !== filters.status) return false;
			if (filters.tech && filters.tech !== 'all' && i.tech !== filters.tech) return false;
			if (filters.severity != null && filters.severity !== 'all' && i.severity !== Number(filters.severity)) return false;
			if (filters.vendor && filters.vendor !== 'all' && i.vendor !== filters.vendor) return false;
			if (filters.siteId && filters.siteId.length > 0) {
				const q = filters.siteId.toLowerCase();
				if (!i.siteId || i.siteId.toLowerCase().indexOf(q) === -1) return false;
			}
			if (filters.cellId && filters.cellId.length > 0) {
				const q = filters.cellId.toLowerCase();
				if (!i.cellId || i.cellId.toLowerCase().indexOf(q) === -1) return false;
			}
			if (filters.durationBucket && filters.durationBucket !== 'all') {
				const d = i.durationDays || 0;
				if (filters.durationBucket === 'lt1'  && !(d < 1))         return false;
				if (filters.durationBucket === '1to3' && !(d >= 1 && d <= 3)) return false;
				if (filters.durationBucket === '4to7' && !(d >= 4 && d <= 7)) return false;
				if (filters.durationBucket === 'gt7'  && !(d > 7))         return false;
			}
			return true;
		});

		const map = {};
		itemsForDist.forEach(function (i) {
			const key = i.tech + '|' + i.kpiName;
			if (!map[key]) {
				map[key] = {
					key: key,
					tech: i.tech,
					kpiName: i.kpiName,
					severity: i.severity,
					total: 0,
					open: 0,
					closed: 0,
				};
			}
			map[key].total += 1;
			if (i.status === 'Open') map[key].open += 1;
			else map[key].closed += 1;
		});

		return Object.keys(map)
			.map(function (k) { return map[k]; })
			.sort(function (a, b) {
				// Ordenar por: severidade (Critical primeiro), depois open desc, depois tech, depois nome
				if (a.severity !== b.severity) return a.severity - b.severity;
				if (a.open !== b.open) return b.open - a.open;
				if (a.tech !== b.tech) return a.tech.localeCompare(b.tech);
				return a.kpiName.localeCompare(b.kpiName);
			});
	}, [items, filters]);

	// --- Listas para dropdowns -------------------------------------------
	const availableVendors = useMemo(function () {
		const set = new Set(items.map(function (i) { return i.vendor; }).filter(Boolean));
		return Array.from(set).sort();
	}, [items]);

	const availableKpis = useMemo(function () {
		const set = new Set(items.map(function (i) { return i.kpiName; }).filter(Boolean));
		return Array.from(set).sort();
	}, [items]);

	const availableTechs = useMemo(function () {
		const set = new Set(items.map(function (i) { return i.tech; }).filter(Boolean));
		return Array.from(set).sort();
	}, [items]);

	return {
		items: items,
		filteredItems: filteredItems,
		loading: loading,
		error: error,
		selected: selected,
		history: history,
		historyLoading: historyLoading,
		filters: filters,
		totalStats: totalStats,
		filteredStats: filteredStats,
		kpiDistribution: kpiDistribution,
		availableVendors: availableVendors,
		availableKpis: availableKpis,
		availableTechs: availableTechs,
		load: load,
		select: select,
		updateFilters: updateFilters,
		clearFilters: clearFilters,
	};
}

// ============================================================================
// useKpiRules
// ============================================================================
export function useKpiRules() {
	const dispatch = useDispatch();

	const items             = useSelector(function (s) { return s.kpiRules.items; });
	const loading           = useSelector(function (s) { return s.kpiRules.loading; });
	const error             = useSelector(function (s) { return s.kpiRules.error; });
	const selectedRule      = useSelector(function (s) { return s.kpiRules.selectedRule; });
	const saving            = useSelector(function (s) { return s.kpiRules.saving; });
	const saveError         = useSelector(function (s) { return s.kpiRules.saveError; });
	const saveSuccess       = useSelector(function (s) { return s.kpiRules.saveSuccess; });
	const deleting          = useSelector(function (s) { return s.kpiRules.deleting; });
	const validation        = useSelector(function (s) { return s.kpiRules.validation; });
	const engineLogs        = useSelector(function (s) { return s.kpiRules.engineLogs; });
	const engineLogsLoading = useSelector(function (s) { return s.kpiRules.engineLogsLoading; });

	const load     = useCallback(function ()     { return dispatch(fetchRules()); },         [dispatch]);
	const save     = useCallback(function (rule) { return dispatch(saveRule(rule)); },       [dispatch]);
	const remove   = useCallback(function (id)   { return dispatch(deleteRule(id)); },       [dispatch]);
	const select   = useCallback(function (rule) { return dispatch(selectRule(rule)); },     [dispatch]);
	const openNew  = useCallback(function ()     { return dispatch(resetRuleForm()); },      [dispatch]);
	const validate = useCallback(function (p)    { return dispatch(validateFormula(p)); },   [dispatch]);
	const clearVal = useCallback(function ()     { return dispatch(resetValidation()); },    [dispatch]);
	const loadLogs = useCallback(function ()     { return dispatch(fetchEngineLogs()); },    [dispatch]);

	const rulesByTech = useMemo(function () {
		return items.reduce(function (acc, r) {
			if (!acc[r.tech]) acc[r.tech] = [];
			acc[r.tech].push(r);
			return acc;
		}, {});
	}, [items]);

	return {
		items: items,
		loading: loading,
		error: error,
		selectedRule: selectedRule,
		saving: saving,
		saveError: saveError,
		saveSuccess: saveSuccess,
		deleting: deleting,
		validation: validation,
		engineLogs: engineLogs,
		engineLogsLoading: engineLogsLoading,
		rulesByTech: rulesByTech,
		load: load,
		save: save,
		remove: remove,
		select: select,
		openNew: openNew,
		validate: validate,
		clearVal: clearVal,
		loadLogs: loadLogs,
	};
}

//
// useSites — sites com coordenadas para o mapa. Expoe tambem um Map() para
// lookup O(1) por chave "vendor|siteId" (usado no IncidentMap).
// =============================================================================
export function useSites() {
	const dispatch = useDispatch();

	const sites = useSelector(function (s) { return s.sites; });

	const load = useCallback(function () {
		return dispatch(fetchSites());
	}, [dispatch]);

	const siteMap = useMemo(function () {
		const map = new Map();
		(sites.items || []).forEach(function (s) {
			map.set(s.vendor + '|' + s.siteId, s);
		});
		return map;
	}, [sites.items]);

	return {
		items:   sites.items,
		loading: sites.loading,
		loaded:  sites.loaded,
		error:   sites.error,
		siteMap: siteMap,
		load:    load,
	};
}